import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Plus, ChevronDown, ChevronRight, Crown, Users as UsersIcon, Settings2, Trash2 } from 'lucide-react';
import type { Department, User } from '../../types';
import { api } from '../../api/client';

const cardStyle: React.CSSProperties = {
    background: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-color)',
    overflow: 'hidden'
};

const headerStyle: React.CSSProperties = {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
};

export function DepartmentManagement() {
    const { departments, createDepartment, updateDepartment, users, setDepartments } = useApp();
    const [newDeptName, setNewDeptName] = useState('');
    const [newDeptColor, setNewDeptColor] = useState('#36c5f0');
    const [expandedDeptId, setExpandedDeptId] = useState<string | null>(null);

    const handleCreateDepartment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newDeptName.trim()) return;

        await createDepartment({
            name: newDeptName,
            color: newDeptColor
        });
        setNewDeptName('');
    };

    // Get users in a department (checking both department_id and department_ids)
    const getDeptUsers = (deptId: string): User[] => {
        return users.filter(u =>
            u.department_id === deptId ||
            (u.department_ids && u.department_ids.includes(deptId))
        );
    };

    const toggleExpand = (deptId: string) => {
        setExpandedDeptId(prev => prev === deptId ? null : deptId);
    };

    const handleToggleHead = async (dept: Department, userId: string) => {
        const currentHeads = dept.head_ids || [];
        const isHead = currentHeads.includes(userId);
        const newHeads = isHead
            ? currentHeads.filter(id => id !== userId)
            : [...currentHeads, userId];
        await updateDepartment(dept.id, { head_ids: newHeads });
    };

    const handleDeleteDepartment = async (dept: Department) => {
        if (!window.confirm(`Are you sure you want to delete the "${dept.name}" department? This cannot be undone.`)) return;
        try {
            await api.deleteDepartment(dept.id);
            setDepartments(prev => prev.filter(d => d.id !== dept.id));
        } catch (err) {
            console.error('Failed to delete department:', err);
        }
    };

    const handleToggleExternalAssignment = async (dept: Department) => {
        await updateDepartment(dept.id, {
            allow_external_individual_assignment: !dept.allow_external_individual_assignment
        });
    };

    return (
        <div style={cardStyle}>
            <div style={headerStyle}>
                <div>
                    <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Departments</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Organize users into functional groups.</p>
                </div>
            </div>

            <div style={{ padding: 20, background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                <form onSubmit={handleCreateDepartment} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <input
                        type="text"
                        className="input"
                        placeholder="New Department Name"
                        value={newDeptName}
                        onChange={e => setNewDeptName(e.target.value)}
                        style={{ flex: 1 }}
                    />
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: 'var(--bg-secondary)',
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: '1px solid var(--border-color)'
                    }}>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Color:</span>
                        <input
                            type="color"
                            value={newDeptColor}
                            onChange={e => setNewDeptColor(e.target.value)}
                            style={{ width: 28, height: 28, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={!newDeptName.trim()}
                        className="btn btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                        <Plus size={16} />
                        Add Department
                    </button>
                </form>
            </div>

            <div>
                {departments.map((dept, i) => {
                    const isExpanded = expandedDeptId === dept.id;
                    const deptUsers = getDeptUsers(dept.id);
                    const heads = deptUsers.filter(u => (dept.head_ids || []).includes(u.id));

                    return (
                        <div key={dept.id} style={{ borderBottom: i < departments.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                            {/* Department Row */}
                            <div
                                onClick={() => toggleExpand(dept.id)}
                                style={{
                                    padding: '16px 20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    background: isExpanded ? 'var(--bg-tertiary)' : 'transparent',
                                    transition: 'background 0.15s'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    <div style={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        background: dept.color,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                    }} />
                                    <span style={{ fontWeight: 500 }}>{dept.name}</span>
                                    {heads.length > 0 && (
                                        <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Crown size={12} color="#f59e0b" />
                                            {heads.map(h => h.name.split(' ')[0]).join(', ')}
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {dept.allow_external_individual_assignment && (
                                        <span style={{
                                            background: 'rgba(34, 197, 94, 0.1)',
                                            color: '#22c55e',
                                            padding: '2px 8px',
                                            borderRadius: 10,
                                            fontSize: 11,
                                            fontWeight: 500
                                        }}>
                                            Open Assignment
                                        </span>
                                    )}
                                    <span style={{
                                        background: 'var(--bg-tertiary)',
                                        padding: '4px 12px',
                                        borderRadius: 12,
                                        fontSize: 12,
                                        color: 'var(--text-muted)'
                                    }}>
                                        {deptUsers.length} members
                                    </span>
                                    <button
                                        className="btn btn-ghost btn-icon btn-sm"
                                        title="Delete department"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteDepartment(dept); }}
                                        style={{ color: 'var(--color-danger, #ef4444)' }}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>

                            {/* Expanded Section */}
                            {isExpanded && (
                                <div style={{ padding: '0 20px 20px', background: 'var(--bg-tertiary)' }}>
                                    {/* Settings */}
                                    <div style={{
                                        background: 'var(--bg-secondary)',
                                        padding: 16,
                                        borderRadius: 8,
                                        marginBottom: 16
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                            <Settings2 size={16} />
                                            <span style={{ fontWeight: 500, fontSize: 14 }}>Department Settings</span>
                                        </div>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 12,
                                            cursor: 'pointer',
                                            padding: '8px 12px',
                                            background: 'var(--bg-tertiary)',
                                            borderRadius: 6
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={dept.allow_external_individual_assignment || false}
                                                onChange={() => handleToggleExternalAssignment(dept)}
                                                style={{ marginTop: 2 }}
                                            />
                                            <div>
                                                <span style={{ fontWeight: 500, fontSize: 13 }}>Allow External Individual Assignment</span>
                                                <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                                                    If enabled, users from other departments can assign tickets directly to individuals in this department.
                                                    If disabled, they can only assign to the department (goes to head's queue).
                                                </p>
                                            </div>
                                        </label>
                                    </div>

                                    {/* Members & Heads */}
                                    <div style={{
                                        background: 'var(--bg-secondary)',
                                        padding: 16,
                                        borderRadius: 8
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                            <UsersIcon size={16} />
                                            <span style={{ fontWeight: 500, fontSize: 14 }}>Members & Heads</span>
                                        </div>

                                        {deptUsers.length === 0 ? (
                                            <p style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
                                                No users assigned to this department yet.
                                            </p>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {deptUsers.map(user => {
                                                    const isHead = (dept.head_ids || []).includes(user.id);
                                                    return (
                                                        <div
                                                            key={user.id}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                padding: '8px 12px',
                                                                background: 'var(--bg-tertiary)',
                                                                borderRadius: 6
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                <div style={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: '50%',
                                                                    background: `hsl(${user.name.charCodeAt(0) * 10 % 360}, 60%, 45%)`,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: 'white',
                                                                    fontWeight: 600,
                                                                    fontSize: 13
                                                                }}>
                                                                    {user.name.charAt(0).toUpperCase()}
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontWeight: 500, fontSize: 13 }}>{user.name}</div>
                                                                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{user.email}</div>
                                                                </div>
                                                                {isHead && (
                                                                    <Crown size={14} color="#f59e0b" style={{ marginLeft: 4 }} />
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleToggleHead(dept, user.id);
                                                                }}
                                                                className={`btn ${isHead ? 'btn-secondary' : 'btn-ghost'}`}
                                                                style={{ fontSize: 12, padding: '4px 10px' }}
                                                            >
                                                                {isHead ? 'Remove as Head' : 'Make Head'}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                {departments.length === 0 && (
                    <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        No departments created yet.
                    </div>
                )}
            </div>
        </div>
    );
}
